import { ChevronRight } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50]

interface PaginationProps {
  page: number
  pageSize: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizeOptions?: number[]
}

export function Pagination({
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: PaginationProps) {
  const handleNextPage = () => {
    if (page < totalPages) {
      onPageChange(page + 1)
    }
  }

  const handlePageSizeChange = (value: string) => {
    onPageSizeChange(Number(value))
  }

  // Generate page numbers to display (show up to 3 pages)
  const getPageNumbers = (): number[] => {
    const pages: number[] = []
    const maxVisible = 3

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
      return pages
    }

    let start = Math.max(1, page - 1)
    const end = Math.min(totalPages, start + maxVisible - 1)

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className="flex items-center justify-between border-t border-[#e7e7e7] px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#556374]">Rows per page:</span>
        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-[70px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map(size => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1">
        {/* Page number buttons */}
        {pageNumbers.map(pageNum => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={cn(
              'h-6 w-6 flex items-center justify-center text-xs font-normal cursor-pointer rounded',
              pageNum === page
                ? 'bg-white border border-[#d6dae1] text-[#556374]'
                : 'text-[#556374] hover:bg-gray-100'
            )}
          >
            {pageNum}
          </button>
        ))}

        {/* Next button */}
        <button
          onClick={handleNextPage}
          disabled={page >= totalPages}
          className={cn(
            'flex items-center gap-0.5 px-2 h-6 text-xs font-medium cursor-pointer',
            page >= totalPages
              ? 'text-[#9ca3af] cursor-not-allowed'
              : 'text-[#556374] hover:text-[#374151]'
          )}
        >
          Next
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
